import { URLS } from 'store/slices/constants/apiV1';
import {
  Availabilities,
  ExpandedPurchaseLine,
  PriceModel,
  Snapshot,
  Invoice,
  InvoicedLink,
  RequestOrApproveSnapshotRequest,
  KittingEstimate,
  KittingOptionsCreate,
  PurchaseLineUpdate,
  ExpandedPurchaseLineGETSchema,
  ConfirmedPurchaseLine,
  UsageInvoiceLine,
} from 'types/purchasing';
import { Part } from 'types/part';
import { ProductionRun } from 'types/production';
import { BomCRUDSchema } from 'types/bom';
import { apiSlice } from 'store/slices/api';
import {
  CreatePurchaseEventRequest,
  Order,
  OrderLine,
  OrderSnapshot,
  OrderUpdates,
} from 'types/order';
import { StockLocation } from 'types/inventory';
import { DateString } from 'types';
import { OrgPartSchema } from 'types/clientV2/parts';

export const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPriceModel: builder.query<PriceModel, PriceModel['id']>({
      query: (priceModelId) => URLS.PRICE_MODEL(priceModelId),
      providesTags: (result) =>
        result
          ? [{ type: 'PriceModel', id: result.id }]
          : [{ type: 'PriceModel', id: 'LIST' }],
    }),
    approveSnapshot: builder.mutation<
      OrderSnapshot,
      { snapshotId: Snapshot['id']; payload: RequestOrApproveSnapshotRequest }
    >({
      query: ({ snapshotId, payload }) => ({
        url: URLS.SNAPSHOT_APPROVE(snapshotId),
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, { snapshotId }) => [
        { type: 'AvailabilitiesSnapshot', id: snapshotId },
        { type: 'OrderSnapshot', id: 'LIST' },
        { type: 'OrderSnapshot', id: snapshotId },
        { type: 'Order', id: 'LIST' },
        { type: 'OrderLines', id: 'LIST' },
        { type: 'PurchaseRequests', id: 'LIST' },
        { type: 'PurchaseOrders', id: 'LIST' },
        { type: 'PurchaseOrderLinesWithPurchase', id: 'LIST' },
        { type: 'ClientV2PurchaseOrderLines', id: 'LIST' },
        { type: 'ClientV2PurchaseRequests', id: 'LIST' },
      ],
    }),
    requestApprovalForSnapshot: builder.mutation<
      Snapshot,
      { snapshotId: Snapshot['id']; payload: RequestOrApproveSnapshotRequest }
    >({
      query: ({ snapshotId, payload }) => ({
        url: URLS.SNAPSHOT_REQUEST_APPROVAL(snapshotId),
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, { snapshotId }) => [
        { type: 'AvailabilitiesSnapshot', id: snapshotId },
        { type: 'OrderSnapshot', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
        { type: 'OrderLines', id: 'LIST' },
        { type: 'ClientV2PurchaseRequests', id: 'LIST' },
      ],
    }),
    rejectSnapshot: builder.mutation<
      OrderSnapshot,
      { snapshotId: Snapshot['id']; payload: { rejectionReason?: string } }
    >({
      query: ({ snapshotId, payload }) => ({
        url: URLS.SNAPSHOT_REJECT(snapshotId),
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, { snapshotId }) => [
        { type: 'AvailabilitiesSnapshot', id: snapshotId },
        { type: 'OrderSnapshot', id: 'LIST' },
        { type: 'OrderSnapshot', id: snapshotId },
        { type: 'Order', id: 'LIST' },
        { type: 'OrderLines', id: 'LIST' },
      ],
    }),
    getSnapshotRecalculateAvailabilities: builder.query<
      Availabilities,
      { snapshotId: OrderSnapshot['id'] }
    >({
      query: ({ snapshotId }) =>
        URLS.SNAPSHOT_RECALCULATE_AVAILABILITIES(snapshotId),
      providesTags: (result, error, { snapshotId }) =>
        result && snapshotId
          ? [
              { type: 'AvailabilitiesSnapshot', id: snapshotId },
              { type: 'AvailabilitiesSnapshot', id: 'LIST' },
            ]
          : [],
    }),
    recalculateAndSaveSnapshot: builder.mutation<
      OrderSnapshot,
      { snapshotId: OrderSnapshot['id']; selectedLead: number }
    >({
      query: ({ snapshotId, selectedLead }) => ({
        url: URLS.SNAPSHOT_SAVE_RECALCULATIONS(snapshotId),
        method: 'PATCH',
        body: { selectedLead },
      }),
      // Not exactly sure yet best way to manage tags for this
      invalidatesTags: (result, error, { snapshotId }) => [
        { type: 'AvailabilitiesSnapshot', id: snapshotId },
        { type: 'OrderSnapshot', id: 'LIST' },
        { type: 'OrderSnapshot', id: snapshotId },
        { type: 'Order', id: 'LIST' },
        { type: 'OrderLines', id: 'LIST' },
      ],
    }),
    getConfirmedPurchaseLines: builder.query<
      ConfirmedPurchaseLine[],
      ExpandedPurchaseLineGETSchema
    >({
      query: ({ facilityId, sandbox }) =>
        URLS.CONFIRMED_PURCHASE_LINES(facilityId, sandbox),
      providesTags: (result) =>
        result
          ? [
              ...result.map(
                ({ id }: ConfirmedPurchaseLine) =>
                  ({
                    type: 'ConfirmedPurchaseLines',
                    id,
                  } as const)
              ),
              { type: 'ConfirmedPurchaseLines', id: 'LIST' },
            ]
          : [{ type: 'ConfirmedPurchaseLines', id: 'LIST' }],
    }),
    updateBatchPurchaseLines: builder.mutation<
      ExpandedPurchaseLine[],
      { orderId: Order['id']; payload: PurchaseLineUpdate[] }
    >({
      query: ({ payload }) => ({
        url: URLS.PURCHASE_LINES(null),
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (result, error, { orderId }) => [
        ...(result
          ? result.map(({ id }: ExpandedPurchaseLine) => ({
              type: 'OrderLines' as 'OrderLines',
              id,
            }))
          : [{ type: 'OrderLines' as 'OrderLines', id: 'LIST' }]),
        { type: 'Order', id: orderId },
      ],
    }),
    getOrders: builder.query<
      Order[],
      {
        productionRunId?: ProductionRun['id'] | null;
        bomId?: BomCRUDSchema['id'] | null;
        part?: Part['id'] | null;
        fromDate?: DateString | null;
        toDate?: DateString | null;
        v2OrgPartId?: OrgPartSchema['id'];
      }
    >({
      query: ({
        productionRunId,
        bomId,
        part,
        fromDate,
        toDate,
        v2OrgPartId = null,
      }) =>
        URLS.ORDERS({
          productionRunId,
          bomId,
          part,
          fromDate,
          toDate,
          v2OrgPartId,
        }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(
                ({ id }: Order) =>
                  ({
                    type: 'Order',
                    id,
                  } as const)
              ),
              { type: 'Order', id: 'LIST' },
            ]
          : [{ type: 'Order', id: 'LIST' }],
    }),
    getOrder: builder.query<Order, Order['id']>({
      query: (orderId) => URLS.ORDER(orderId),
      providesTags: (result, error, orderId) => [
        { type: 'Order', id: orderId },
      ],
    }),
    getOrderLines: builder.query<OrderLine[], Order['id']>({
      query: (orderId) => URLS.ORDER_LINES(orderId),
      providesTags: (result) =>
        result
          ? [
              ...result.map(
                ({ id }: OrderLine) =>
                  ({
                    type: 'OrderLines',
                    id,
                  } as const)
              ),
              { type: 'OrderLines', id: 'LIST' },
            ]
          : [{ type: 'OrderLines', id: 'LIST' }],
    }),
    createPurchaseEvent: builder.mutation<
      OrderUpdates[],
      {
        payload: CreatePurchaseEventRequest;
      }
    >({
      query: ({ payload }) => ({
        url: URLS.PURCHASE_EVENTS,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: () => [
        { type: 'Order', id: 'LIST' },
        { type: 'OrderUpdates', id: 'LIST' },
        { type: 'OrderLines', id: 'LIST' },
        { type: 'ExpandedPurchaseLines', id: 'LIST' },
        { type: 'PurchaseOrderLines', id: 'LIST' },
        { type: 'PurchaseOrderEvents', id: 'LIST' },
        { type: 'PurchaseOrderLinesWithPurchase', id: 'LIST' },
      ],
    }),
    getOrderSnapshot: builder.query<OrderSnapshot, OrderSnapshot['id']>({
      query: (snapshotId) => URLS.SNAPSHOT(snapshotId),
      providesTags: (_result, _error, snapshotId) => [
        { type: 'OrderSnapshot', id: snapshotId },
      ],
    }),
    updateOrderSnapshot: builder.mutation<
      OrderSnapshot,
      {
        id: OrderSnapshot['id'];
        payload: {
          externalPoNumber?: string;
          externalPoTotal?: number;
          productionRun?: ProductionRun['id'];
          archived?: boolean;
        };
      }
    >({
      query: ({ id, payload }) => ({
        url: URLS.SNAPSHOT(id),
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'OrderSnapshot', id },
        { type: 'OrderSnapshot', id: 'LIST' },
      ],
    }),
    getOrderSnapshots: builder.query<
      OrderSnapshot[],
      {
        productionRunId?: ProductionRun['id'] | null;
        fromDate?: DateString | null;
        toDate?: DateString | null;
        sandbox?: boolean;
        bomId?: BomCRUDSchema['id'] | null;
      }
    >({
      query: ({ productionRunId, fromDate, toDate, sandbox, bomId }) =>
        URLS.SNAPSHOTS({
          productionRunId,
          fromDate,
          toDate,
          sandbox,
          bomId,
        }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(
                ({ id }: OrderSnapshot) =>
                  ({
                    type: 'OrderSnapshot',
                    id,
                  } as const)
              ),
              { type: 'OrderSnapshot', id: 'LIST' },
            ]
          : [{ type: 'OrderSnapshot', id: 'LIST' }],
    }),
    getInvoices: builder.query<Invoice[], { fromDate: string; toDate: string }>(
      {
        query: ({ fromDate, toDate }) => URLS.INVOICES(fromDate, toDate),
        providesTags: (result) =>
          result
            ? [
                ...result.map(
                  ({ id }: Invoice) => ({ type: 'Invoice', id } as const)
                ),
                { type: 'Invoice', id: 'LIST' },
              ]
            : [{ type: 'Invoice', id: 'LIST' }],
      }
    ),
    getInvoice: builder.query<Invoice, string>({
      query: (invoiceNumber) => URLS.INVOICE(invoiceNumber),
      providesTags: (result) =>
        result
          ? [{ type: 'Invoice', id: result.id }]
          : [{ type: 'Invoice', id: 'LIST' }],
    }),
    getInvoicedLink: builder.query<InvoicedLink, void>({
      query: () => ({
        url: URLS.INVOICED_LINK,
      }),
    }),
    getStockLocationKittingOptions: builder.query<
      KittingEstimate,
      { id: StockLocation['id']; payload: KittingOptionsCreate }
    >({
      query: ({ id, payload }) => ({
        url: URLS.STOCK_LOCATION_KITTING_OPTIONS(id),
        method: 'POST',
        body: payload,
      }),
      providesTags: [{ type: 'KittingOptions', id: 'LIST' }],
    }),
    markOrderLineUnshipped: builder.mutation<
      OrderLine,
      { orderId: Order['id']; orderLineId: OrderLine['id'] }
    >({
      query: ({ orderId, orderLineId }) => ({
        url: URLS.ORDER_LINE_UNSHIP({ orderId, orderLineId }),
        method: 'POST',
      }),
      invalidatesTags: (result, error, { orderId }) =>
        result
          ? [
              { type: 'Order', id: orderId },
              { type: 'Order', id: 'LIST' },
              { type: 'OrderLines', id: orderId },
              { type: 'OrderLines', id: 'LIST' },
              { type: 'OrderUpdates', id: orderId },
            ]
          : [],
    }),
    getUsageInvoice: builder.query<UsageInvoiceLine[], void>({
      query: () => URLS.USAGE_INVOICE,
    }),
  }),
});

export const {
  useGetPriceModelQuery,
  useApproveSnapshotMutation,
  useRequestApprovalForSnapshotMutation,
  useRejectSnapshotMutation,
  useLazyGetSnapshotRecalculateAvailabilitiesQuery,
  useRecalculateAndSaveSnapshotMutation,
  useGetConfirmedPurchaseLinesQuery,
  useUpdateBatchPurchaseLinesMutation,
  useGetOrderSnapshotQuery,
  useUpdateOrderSnapshotMutation,
  useGetOrderSnapshotsQuery,
  useGetOrdersQuery,
  useGetOrderQuery,
  useGetOrderLinesQuery,
  useCreatePurchaseEventMutation,
  useGetInvoicesQuery,
  useGetInvoiceQuery,
  useGetInvoicedLinkQuery,
  useGetStockLocationKittingOptionsQuery,
  useMarkOrderLineUnshippedMutation,
  useGetUsageInvoiceQuery,
} = extendedApiSlice;
